import React, { useEffect } from "react";
import Map from "./components/Map";
import RouteSelector from "./components/RouteSelector";
import ResultComponent from "./components/ResultComponent";
import { getAvailableRoutes, getRoutes, getPreprocessedRoutes, getSchools } from "./services/routeService";
import { RouteState, RouteSelection } from "./types";
import { ThemeProvider, useTheme } from './context/ThemeContext';

const useRoutes = () => {
  const [state, setState] = React.useState<RouteState>({
    selectedCity: "",
    selectedRoutes: [],
    randomRouteCount: 0,
    fetchedRoutes: null,
    fetchedSchools: null,
    hiddenRoutes: [],
    loading: true,
    error: null,
    availableRoutes: [],
    numberOfPreprocessedRoutes: [],
    preprocessed: false,
    availableCities: [],
  });

  const handleRoutesSelected = (selection: RouteSelection) => {
    setState(prev => ({
      ...prev,
      selectedCity: selection.city,
      selectedRoutes: selection.routes,
      randomRouteCount: selection.routeCount,
      preprocessed: selection.isPreprocessed,
    }));
  };

  // Fetch the available routes
  useEffect(() => {
    const fetchAvailableRoutes = async () => {
      try {
        const data = await getAvailableRoutes();
        setState(prev => ({
          ...prev,
          availableRoutes: data.available_routes,
          numberOfPreprocessedRoutes: data.number_preprocessed_routes,
          availableCities: data.available_cities,
        }));
      } catch (error) {
        setState(prev => ({ ...prev, error: "Failed to fetch available routes" }));
      } finally {
        setState(prev => ({ ...prev, loading: false }));
      }
    };
    fetchAvailableRoutes();
  }, []);

  // Fetch the routes based on the selected routes
  useEffect(() => {
    const fetchSelectedRoutes = async () => {
      if (!state.preprocessed && (state.selectedRoutes.length === 0 || state.randomRouteCount === 0)) {
        setState(prev => ({ ...prev, fetchedRoutes: null }));
        return;
      }
      try {
        setState(prev => ({ ...prev, loading: true, error: null }));
        if (state.preprocessed) {
          const data = await getPreprocessedRoutes(state.selectedCity, state.randomRouteCount);
          setState(prev => ({ ...prev, fetchedRoutes: data }));
        } else {
          const data = await getRoutes(state.selectedCity, state.selectedRoutes, state.randomRouteCount);
          setState(prev => ({ ...prev, fetchedRoutes: data }));
        }
      } catch (error) {
        setState(prev => ({ ...prev, error: "Failed to fetch selected routes" }));
      } finally {
        setState(prev => ({ ...prev, loading: false }));
      }
    };
    fetchSelectedRoutes();
  }, [state.selectedCity, state.selectedRoutes, state.randomRouteCount, state.preprocessed]);

  // Fetch the schools based on the selected city
  useEffect(() => {
    const fetchSchools = async () => {
      try {
        const data = await getSchools(state.selectedCity);
        setState(prev => ({ ...prev, fetchedSchools: data }));
      } catch (error) {
        setState(prev => ({ ...prev, error: "Failed to fetch schools", fetchedSchools: null }));
      }
    };

    if (state.selectedCity) {
      fetchSchools();
    }
  }, [state.selectedCity]);

  return {
    state,
    handleRoutesSelected,
  };
};

// Main App component
const App: React.FC = () => (
  <ThemeProvider>
    <AppContent {...useRoutes()} />
  </ThemeProvider>
);

const AppContent: React.FC<ReturnType<typeof useRoutes>> = ({ state, handleRoutesSelected }) => {
  const { darkMode, toggleDarkMode } = useTheme();
  const {
    loading,
    error,
    availableCities,
    availableRoutes,
    numberOfPreprocessedRoutes,
    fetchedRoutes,
    randomRouteCount,
    fetchedSchools,
  } = state;

  return (
    <div className="container">
      <div className="controls">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
          <h1 style={{ margin: 0 }}>SUSTAINABLE ROUTING LABORATORY</h1>
          <button
            onClick={toggleDarkMode}
            className="darkmode-button"
          >
            {darkMode ? '☀️' : '🌙'}
          </button>
        </div>
        {loading && <LoadingSpinner />}
        {error && <ErrorMessage message={error} />}
        <RouteSelector
          availableCities={availableCities}
          availableRoutes={availableRoutes}
          availablePreprocessedRoutes={numberOfPreprocessedRoutes}
          onRoutesSelected={handleRoutesSelected}
        />
        <ResultComponent
          fetchedRoutes={fetchedRoutes}
          randomRouteCount={randomRouteCount}
        />
      </div>
      <div className="map-container">
        <Map fetchedRoutes={fetchedRoutes} fetchedSchools={fetchedSchools} />
      </div>
    </div>
  );
};

const LoadingSpinner: React.FC = () => (
  <div className="loading-spinner">
    <div className="spinner"></div>
    <p>Loading...</p>
  </div>
);

const ErrorMessage: React.FC<{ message: string }> = ({ message }) => (
  <p className="error-message">{message}</p>
);

export default App;
